import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

/**
 * WebSocket Provider for Real-time Updates
 * 
 * Manages WebSocket connection for real-time task progress updates.
 * Automatically reconnects on disconnection.
 */
export function WebSocketProvider({ children }) {
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // Get WebSocket URL from environment
  const getWsUrl = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    // Use current window location for WebSocket to avoid cross-origin issues
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsHost = window.location.host;
    
    return `${wsProtocol}://${wsHost}/ws/${token}`;
  }, []);

  // Handle incoming messages
  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'connected':
          console.log('WebSocket connected:', data);
          setIsConnected(true);
          break;
          
        case 'pong':
          // Keep-alive response, no action needed
          break;
          
        case 'task_progress_update':
          // Real-time task progress update
          setLastUpdate({
            type: 'progress',
            ...data,
            receivedAt: new Date().toISOString()
          });
          
          // Show toast notification
          const progressText = data.changes?.progress_percentage !== undefined
            ? `${data.changes.progress_percentage}%`
            : data.changes?.status?.replace('_', ' ');
          
          toast.info(
            `📊 ${data.task_name} updated to ${progressText}`,
            {
              description: `Updated by ${data.updated_by}`,
              duration: 4000
            }
          );
          break;
          
        case 'subscribed':
          console.log('Subscribed to project:', data.project_id);
          break;
          
        default:
          console.log('Unknown WebSocket message:', data);
      }
    } catch (error) {
      console.error('WebSocket message parse error:', error);
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    const wsUrl = getWsUrl();
    if (!wsUrl) {
      console.log('No token available for WebSocket connection');
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connection opened');
        setIsConnected(true);
        
        // Start ping interval for keep-alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000); // Ping every 30 seconds
      };

      ws.onmessage = handleMessage;

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        // Attempt reconnection after 5 seconds (if not intentional close)
        if (event.code !== 1000 && event.code !== 4001 && event.code !== 4002) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting WebSocket reconnection...');
            connect();
          }, 5000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
    }
  }, [getWsUrl, handleMessage]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }
    
    setIsConnected(false);
  }, []);

  // Subscribe to a specific project
  const subscribeToProject = useCallback((projectId) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe_project',
        project_id: projectId
      }));
    }
  }, []);

  // Auto-connect when user is logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  // Listen for login/logout events
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          // Token added - connect
          setTimeout(connect, 1000); // Slight delay to ensure token is stored
        } else {
          // Token removed - disconnect
          disconnect();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [connect, disconnect]);

  const value = {
    isConnected,
    lastUpdate,
    connect,
    disconnect,
    subscribeToProject
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Hook to use WebSocket context
 */
export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

/**
 * Hook to subscribe to real-time task updates
 * Automatically refetches data when updates are received for the specified project
 */
export function useRealtimeTaskUpdates(projectId, onUpdate) {
  const { lastUpdate, subscribeToProject, isConnected } = useWebSocket();

  // Subscribe to project on mount
  useEffect(() => {
    if (isConnected && projectId) {
      subscribeToProject(projectId);
    }
  }, [isConnected, projectId, subscribeToProject]);

  // Call onUpdate when relevant update is received
  useEffect(() => {
    if (lastUpdate && lastUpdate.type === 'progress') {
      // If projectId is specified, only trigger for that project
      if (!projectId || lastUpdate.project_id === projectId) {
        onUpdate?.(lastUpdate);
      }
    }
  }, [lastUpdate, projectId, onUpdate]);

  return { lastUpdate, isConnected };
}

export default WebSocketContext;
